import { EventPoster } from '@/features/events/shared/EventPoster';

// Web map is intentionally split so browser layout can evolve without native sheet constraints.
import { EventMap } from '@/features/map/EventMap';
import { VenueMap } from '@/features/map/VenueMap.web';
import { Icon } from '@/components/ui/icon';
import { AppScreen, MetaBadge } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import {
  type EventTagGroup,
  getEventAttributeLabel,
  getEventTagLabel,
  type EventTagValue,
} from '@/features/events/eventTags';
import { getResolvedFeedEntryBadgeLabel } from '@/features/events/eventMobileHelpers';
import { useEventTagCatalog } from '@/features/events/useEventTagCatalog';
import { ENTRY_TYPE_FILTERS, type EntryTypeFilter } from '@/features/events/feed/eventsFeedShared';
import { useMapEvents } from '@/features/map/useMapEvents';
import { useNearbyVenues } from '@/features/map/useNearbyVenues';
import { useVenueEvents } from '@/features/venues/useVenueEvents';
import {
  formatDealTimeRange,
  getDealTiming,
  sortDealsByTiming,
} from '@/features/venues/deals/deals';
import { getVenueCategoryConfig, resolveVenueCategory } from '@/features/venues/venueCategories';
import { APP_CONFIG } from '@/lib/app/config';
import {
  trackEventLinkTapped,
  trackEventShare,
  trackMapEventPinTapped,
  trackMapFilterApplied,
  trackMapOpened,
  trackMapVenuePinTapped,
  trackMapVenueTap,
  trackVenueDirectionsTapped,
  trackVenueMapAppearance,
} from '@/lib/analytics/posthog';
import { formatApproximateDistanceLabel, useUserOriginCoords } from '@/lib/location/locationOrigin';
import {
  EntryTypeCover,
  EntryTypeFree,
  EntryTypeFreeRSVP,
  EntryTypeRSVP,
  EntryTypeTicketed,
  type VenueCategory,
  type VenueDealCategory,
  type VenuePublicDeal,
} from '@/lib/api/apiTypes';
import {
  VENUE_DEAL_CATEGORY_FILTER_OPTIONS,
  getVenueDealCategoryColor,
  getVenueDealCategoryLabel,
} from '@radious/catalog/deal-categories';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { type DiscoveryEvent as RadiousEvent } from '@/lib/api/apiTypes';
import { formatEventDayLabel, formatEventTimeRange, getRelativeTime } from '@/features/events/eventFormatting';
import { getEventHref, getPublicIdentityHref, getVenueHref } from '@/lib/navigation/routes';
import { showToast } from '@/lib/useToastStore';
import { shareLink } from '@/lib/platform/share';
import { router } from 'expo-router';
import {
  Building2Icon,
  CheckIcon,
  ChevronDownIcon,
  Clock3Icon,
  FlameIcon,
  LocateFixedIcon,
  MapPinIcon,
  MapPinnedIcon,
  NavigationIcon,
  Share2Icon,
  SlidersHorizontalIcon,
  SparklesIcon,
  TicketPercentIcon,
  XIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { openAddressDirections, openDirections } from '@/lib/location/directions';
import { Pressable, ScrollView, useWindowDimensions, View } from 'react-native';

const LIVE_MODE_TOAST = 'Nothing live nearby. Switched to All Today for later events.';
const WEB_FALLBACK_REGION: WebMapRegion = {
  latitude: 29.4241,
  longitude: -98.4936,
  latitudeDelta: 0.08,
  longitudeDelta: 0.08,
};
const MAP_DEAL_FILTER_CATEGORIES: VenueDealCategory[] =
  VENUE_DEAL_CATEGORY_FILTER_OPTIONS.map((option) => option.value as VenueDealCategory);
const MAP_VENUE_CATEGORY_GROUPS: { label: string; values: VenueCategory[] }[] = [
  { label: 'Music', values: ['music_venue'] },
  {
    label: 'Drinks',
    values: ['bar', 'dive_bar', 'wine_bar', 'brewery', 'distillery', 'hookah_lounge'],
  },
  { label: 'Nightlife', values: ['nightclub', 'lounge', 'speakeasy', 'gay_bar'] },
  { label: 'Stage', values: ['comedy_club', 'karaoke'] },
  { label: 'Games', values: ['sports_bar', 'pool_hall'] },
  { label: 'Rooftop', values: ['rooftop'] },
  { label: 'Other', values: ['other'] },
];
const MAP_FILTER_POPOVER_ID = 'map-filter-popover';

type EventMapMode = 'live' | 'today';
type WebDiscoveryMode = 'events' | 'venues';
type MapFilterMenu =
  | 'eventEntry'
  | 'eventScene'
  | 'venueCategory'
  | 'venueDeals'
  | 'venueActivity'
  | null;
type VenueActivityFilter = 'any' | 'live' | 'upcoming';
type WebMapRegion = {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
};

export default function MapScreen() {
  const { width } = useWindowDimensions();
  const isCompactWeb = width < 720;
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [selectedVenueId, setSelectedVenueId] = React.useState<string | null>(null);
  const [discoveryMode, setDiscoveryMode] = React.useState<WebDiscoveryMode>('events');
  const [eventMapMode, setEventMapMode] = React.useState<EventMapMode>('live');
  const [selectedEventTag, setSelectedEventTag] = React.useState<EventTagValue | ''>('');
  const [eventEntryFilter, setEventEntryFilter] = React.useState<EntryTypeFilter>('all');
  const [selectedVenueCategories, setSelectedVenueCategories] = React.useState<string[]>([]);
  const [showDealVenuesOnly, setShowDealVenuesOnly] = React.useState(false);
  const [selectedDealCategories, setSelectedDealCategories] = React.useState<VenueDealCategory[]>(
    MAP_DEAL_FILTER_CATEGORIES
  );
  const [venueActivityFilter, setVenueActivityFilter] = React.useState<VenueActivityFilter>('any');
  const [mapFilterMenu, setMapFilterMenu] = React.useState<MapFilterMenu>(null);
  const tagCatalogQuery = useEventTagCatalog(discoveryMode === 'events');
  const userOrigin = useUserOriginCoords();
  const recenterRef = React.useRef<(() => void) | null>(null);
  const mapAnalyticsSessionRef = React.useRef(`web-map-${Date.now()}`);
  const trackedVenueAppearanceRef = React.useRef(new Set<string>());
  const initialRegion = React.useMemo(
    () => ({
      ...WEB_FALLBACK_REGION,
      latitude: userOrigin.latitude,
      longitude: userOrigin.longitude,
    }),
    [userOrigin.latitude, userOrigin.longitude]
  );
  const [mapRegion, setMapRegion] = React.useState<WebMapRegion>(initialRegion);
  const [queryRegion, setQueryRegion] = React.useState<WebMapRegion>(initialRegion);
  const mapEventsQuery = useMapEvents({
    enabled: discoveryMode === 'events',
    latitude: queryRegion.latitude,
    longitude: queryRegion.longitude,
    originLatitude: userOrigin.latitude,
    originLongitude: userOrigin.longitude,
    timeWindow: eventMapMode,
  });
  const serverVenueCategory =
    selectedVenueCategories.length === 1 ? selectedVenueCategories[0] : '';
  const nearbyVenuesQuery = useNearbyVenues({
    enabled: discoveryMode === 'venues',
    latitude: queryRegion.latitude,
    longitude: queryRegion.longitude,
    originLatitude: userOrigin.latitude,
    originLongitude: userOrigin.longitude,
    radiusMiles: 10,
    category: serverVenueCategory,
    limit: 100,
  });
  const events = React.useMemo(
    () => mapEventsQuery.data?.events ?? [],
    [mapEventsQuery.data?.events]
  );
  const filteredEvents = React.useMemo(() => {
    return events.filter((event) => {
      if (eventEntryFilter !== 'all' && !eventMatchesEntryFilter(event, eventEntryFilter)) {
        return false;
      }
      if (selectedEventTag && !event.tags.includes(selectedEventTag)) {
        return false;
      }
      return true;
    });
  }, [eventEntryFilter, events, selectedEventTag]);
  const venues = React.useMemo(() => {
    let filteredVenues = nearbyVenuesQuery.data?.venues ?? [];

    if (selectedVenueCategories.length > 1) {
      const selectedCategories = new Set(selectedVenueCategories);
      filteredVenues = filteredVenues.filter((venue) => {
        const category = resolveVenueCategory(venue.category);
        return category ? selectedCategories.has(category) : false;
      });
    }

    if (showDealVenuesOnly) {
      const selectedDealCategorySet = new Set(selectedDealCategories);
      filteredVenues = filteredVenues.filter((venue) =>
        hasVenueMapDealByCategories(venue.deals, selectedDealCategorySet)
      );
    }

    if (venueActivityFilter === 'live') {
      filteredVenues = filteredVenues.filter((venue) => venue.has_live_event);
    } else if (venueActivityFilter === 'upcoming') {
      filteredVenues = filteredVenues.filter((venue) => venue.upcoming_event_count > 0);
    }

    return filteredVenues;
  }, [
    nearbyVenuesQuery.data?.venues,
    selectedDealCategories,
    selectedVenueCategories,
    showDealVenuesOnly,
    venueActivityFilter,
  ]);
  const mapEventsSettled = !mapEventsQuery.isPending && !mapEventsQuery.isFetching;
  const venueQuerySettled = !nearbyVenuesQuery.isPending && !nearbyVenuesQuery.isFetching;

  React.useEffect(() => {
    trackMapOpened(null, 'web');
  }, []);

  React.useEffect(() => {
    if (discoveryMode !== 'events') {
      return;
    }
    if (!filteredEvents.length) {
      setSelectedId(null);
      return;
    }
    if (selectedId && !filteredEvents.some((e) => e.id === selectedId)) setSelectedId(null);
  }, [discoveryMode, filteredEvents, selectedId]);

  React.useEffect(() => {
    if (discoveryMode !== 'venues') {
      return;
    }
    if (!venues.length) {
      setSelectedVenueId(null);
      return;
    }
    if (selectedVenueId && !venues.some((venue) => venue.id === selectedVenueId)) {
      setSelectedVenueId(null);
    }
  }, [discoveryMode, selectedVenueId, venues]);

  React.useEffect(() => {
    if (!webRegionChangedMeaningfully(queryRegion, mapRegion)) {
      return;
    }

    const timeout = setTimeout(() => setQueryRegion(mapRegion), 700);
    return () => clearTimeout(timeout);
  }, [mapRegion, queryRegion]);

  React.useEffect(() => {
    if (!mapFilterMenu || typeof document === 'undefined') {
      return;
    }

    function closeFilterMenuFromOutsidePress(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest(`#${MAP_FILTER_POPOVER_ID}`)) {
        setMapFilterMenu(null);
      }
    }

    document.addEventListener('pointerdown', closeFilterMenuFromOutsidePress, true);
    return () => {
      document.removeEventListener('pointerdown', closeFilterMenuFromOutsidePress, true);
    };
  }, [mapFilterMenu]);

  React.useEffect(() => {
    if (
      discoveryMode !== 'events' ||
      eventMapMode !== 'live' ||
      !mapEventsSettled ||
      filteredEvents.length > 0
    ) {
      return;
    }

    setEventMapMode('today');
    showToast({ message: LIVE_MODE_TOAST });
  }, [discoveryMode, eventMapMode, filteredEvents.length, mapEventsSettled]);

  const selectedEvent = filteredEvents.find((e) => e.id === selectedId) ?? null;
  const selectedVenue = venues.find((venue) => venue.id === selectedVenueId) ?? null;
  const selectedVenueEventsQuery = useVenueEvents(
    discoveryMode === 'venues' ? selectedVenue?.id : null
  );
  const closePanel = React.useCallback(() => {
    setSelectedId(null);
    setSelectedVenueId(null);
    setMapFilterMenu(null);
  }, []);
  const selectedEntryLabel = selectedEvent ? getResolvedFeedEntryBadgeLabel(selectedEvent) : null;
  const selectedRelativeTime = selectedEvent
    ? getRelativeTime(selectedEvent.starts_at, selectedEvent.ends_at)
    : null;
  const selectedMetadata = selectedEvent
    ? [
        ...selectedEvent.tags.map(getEventTagLabel),
        ...selectedEvent.attributes.map(getEventAttributeLabel),
      ].slice(0, 4)
    : [];
  const selectedDistanceLabel = selectedEvent
    ? formatApproximateDistanceLabel(selectedEvent.distance_miles, userOrigin)
    : null;
  const selectedVenueDeals = selectedVenue
    ? sortDealsByTiming(getSafeVenueDeals(selectedVenue.deals)).filter(isVenueMapDeal).slice(0, 3)
    : [];
  const selectedVenueEvents = (selectedVenueEventsQuery.data?.events ?? []).slice(0, 3);
  const selectedVenueCategoryConfig = React.useMemo(
    () => getVenueCategoryConfig(resolveVenueCategory(selectedVenue?.category)),
    [selectedVenue?.category]
  );
  const venueFilterSummary = React.useMemo(
    () => getMapVenueCategorySummary(selectedVenueCategories),
    [selectedVenueCategories]
  );
  const dealFilterSummary =
    selectedDealCategories.length < MAP_DEAL_FILTER_CATEGORIES.length
      ? `Deals · ${selectedDealCategories.length}`
      : 'Deals';
  const eventEntryFilterSummary = getMapEntryFilterLabel(eventEntryFilter);
  const eventSceneFilterSummary = selectedEventTag
    ? getEventTagLabel(selectedEventTag)
    : 'All scenes';
  const eventSceneFilterGroups = React.useMemo(
    () => buildMapSceneFilterGroups(events, tagCatalogQuery.data),
    [events, tagCatalogQuery.data]
  );
  const venueActivityFilterSummary = getVenueActivityFilterLabel(venueActivityFilter);

  React.useEffect(() => {
    if (discoveryMode !== 'venues') {
      return;
    }

    venues.slice(0, 30).forEach((venue, index) => {
      const key = `${venue.id}:${venueFilterSummary}:${dealFilterSummary}:${venueActivityFilter}`;
      if (trackedVenueAppearanceRef.current.has(key)) {
        return;
      }
      trackedVenueAppearanceRef.current.add(key);
      trackVenueMapAppearance({
        position: index,
        sessionKey: mapAnalyticsSessionRef.current,
        surface: 'web',
        venueID: venue.id,
      });
    });
  }, [dealFilterSummary, discoveryMode, venueActivityFilter, venueFilterSummary, venues]);

  function handleDirections() {
    if (!selectedEvent) return;
    trackEventLinkTapped({
      eventID: selectedEvent.id,
      linkType: 'directions',
      source: 'map',
      surface: 'web',
      venueID: selectedEvent.venue_id,
    });
    const { latitude, longitude } = selectedEvent.coordinate;
    openDirections(latitude, longitude).catch(() => undefined);
  }

  async function handleEventShare() {
    if (!selectedEvent) return;

    const href = getEventHref(selectedEvent.id, selectedEvent.title);
    const result = await shareLink({
      message: `${selectedEvent.title} at ${selectedEvent.venue_name} on Radious`,
      title: selectedEvent.title,
      url: getAbsoluteHref(href),
    });
    if (result !== 'failed') {
      trackEventShare({
        eventID: selectedEvent.id,
        source: 'map',
        shareMethod: result === 'shared' ? 'native_share' : 'copy_link',
        surface: 'web',
        venueID: selectedEvent.venue_id,
      });
    }
  }

  function handleVenueDirections() {
    if (!selectedVenue?.address) return;
    trackVenueDirectionsTapped({
      surface: 'web_map',
      venueID: selectedVenue.id,
      venueName: selectedVenue.name,
    });
    openAddressDirections(selectedVenue.address).catch(() => undefined);
  }

  function handleDiscoveryModeChange(nextMode: WebDiscoveryMode) {
    setDiscoveryMode(nextMode);
    setSelectedId(null);
    setSelectedVenueId(null);
    setMapFilterMenu(null);
    trackMapFilterApplied('mode', nextMode, 'web');
  }

  function applyVenueCategoryGroup(categories: VenueCategory[]) {
    setSelectedVenueId(null);
    setSelectedVenueCategories((current) =>
      areSameVenueCategorySelection(current, categories) ? [] : categories
    );
    setMapFilterMenu(null);
    trackMapFilterApplied('category', categories.join(',') || 'all', 'web');
  }

  function toggleDealCategory(category: VenueDealCategory) {
    setSelectedVenueId(null);
    setSelectedDealCategories((current) => {
      if (current.includes(category)) {
        const next = current.filter((value) => value !== category);
        if (!next.length) {
          return current;
        }
        trackMapFilterApplied('venue_deal_categories', next.join(','), 'web');
        return next;
      }
      const next = [...current, category];
      trackMapFilterApplied('venue_deal_categories', next.join(','), 'web');
      return next;
    });
  }

  function handleRegionChange(nextRegion: WebMapRegion) {
    setMapRegion((previousRegion) =>
      webRegionChangedMeaningfully(previousRegion, nextRegion) ? nextRegion : previousRegion
    );
  }

  return (
    <AppScreen immersive>
      <View style={{ flex: 1, position: 'relative' }}>
        {/* Map fills everything */}
        {discoveryMode === 'events' ? (
          <EventMap
            events={filteredEvents}
            selectedId={selectedEvent?.id ?? null}
            initialRegion={initialRegion}
            onSelect={(eventOrID: unknown) => {
              if (typeof eventOrID === 'string') {
                setSelectedId(eventOrID);
                trackMapEventPinTapped({ eventID: eventOrID, surface: 'web' });
                return;
              }
              if (eventOrID && typeof eventOrID === 'object' && 'id' in eventOrID) {
                const eventID = String(eventOrID.id);
                const venueID =
                  'venue_id' in eventOrID && typeof eventOrID.venue_id === 'string'
                    ? eventOrID.venue_id
                    : null;
                setSelectedId(eventID);
                trackMapEventPinTapped({ eventID, surface: 'web', venueID });
              }
            }}
            onMapMove={closePanel}
            onRegionChangeComplete={handleRegionChange}
            onRecenter={(fn) => {
              recenterRef.current = fn;
            }}
          />
        ) : (
          <VenueMap
            venues={venues}
            selectedId={selectedVenueId}
            initialRegion={initialRegion}
            onSelect={(venue) => {
              setSelectedVenueId(venue.id);
              trackMapVenuePinTapped(venue.id, 'web');
              trackMapVenueTap({
                venueID: venue.id,
                venueName: venue.name,
                surface: 'web',
              });
            }}
            onMapMove={closePanel}
            onRegionChangeComplete={handleRegionChange}
            onRecenter={(fn) => {
              recenterRef.current = fn;
            }}
          />
        )}

        <MapStatusOverlay
          isError={discoveryMode === 'events' ? mapEventsQuery.isError : nearbyVenuesQuery.isError}
          isLoading={
            discoveryMode === 'events' ? mapEventsQuery.isPending : nearbyVenuesQuery.isPending
          }
          isEmpty={
            discoveryMode === 'events'
              ? mapEventsSettled && filteredEvents.length === 0
              : venueQuerySettled && venues.length === 0
          }
          hasFilter={
            discoveryMode === 'events'
              ? Boolean(selectedEventTag) || eventEntryFilter !== 'all'
              : selectedVenueCategories.length > 0 ||
                showDealVenuesOnly ||
                venueActivityFilter !== 'any'
          }
          mode={eventMapMode}
          subject={discoveryMode}
          onRetry={() =>
            discoveryMode === 'events' ? mapEventsQuery.refetch() : nearbyVenuesQuery.refetch()
          }
          onClearFilter={() => {
            setSelectedEventTag('');
            setEventEntryFilter('all');
            setSelectedVenueCategories([]);
            setShowDealVenuesOnly(false);
            setSelectedDealCategories(MAP_DEAL_FILTER_CATEGORIES);
            setVenueActivityFilter('any');
            setMapFilterMenu(null);
            trackMapFilterApplied('reset', 'all', 'web');
          }}
          onShowToday={() => {
            setEventMapMode('today');
            trackMapFilterApplied('event_map_mode', 'today', 'web');
          }}
        />

        {mapFilterMenu ? (
          <Pressable
            onPress={() => setMapFilterMenu(null)}
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 9,
            }}
          />
        ) : null}

        {/* Floating header */}
        <View
          pointerEvents="box-none"
          style={
            {
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 10,
              width: 424,
              maxWidth: 'calc(100% - 32px)',
              paddingLeft: 16,
              paddingTop: 12,
            } as any
          }>
          <View
            style={{
              borderRadius: 20,
              borderWidth: 1,
              borderColor: DESIGN_TOKENS.colors.borderStrong,
              backgroundColor: DESIGN_TOKENS.colors.surfaceOverlay,
              shadowColor: '#000',
              shadowOpacity: 0.22,
              shadowRadius: 20,
              shadowOffset: { width: 0, height: 8 },
              overflow: 'visible',
            }}>
            {/* Top bar: mode switcher + pin count */}
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingHorizontal: 10,
                paddingVertical: 8,
                gap: 10,
              }}>
              {/* Segmented mode control */}
              <View
                style={{
                  flexDirection: 'row',
                  borderRadius: 999,
                  backgroundColor: DESIGN_TOKENS.colors.surface,
                  borderWidth: 1,
                  borderColor: DESIGN_TOKENS.colors.border,
                  padding: 3,
                  gap: 2,
                }}>
                {(['events', 'venues'] as const).map((mode) => {
                  const isActive = discoveryMode === mode;
                  return (
                    <Pressable
                      key={mode}
                      onPress={() => handleDiscoveryModeChange(mode)}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 5,
                        paddingHorizontal: 13,
                        paddingVertical: 6,
                        borderRadius: 999,
                        backgroundColor: isActive ? DESIGN_TOKENS.colors.accent : 'transparent',
                      }}>
                      <Icon
                        as={mode === 'events' ? MapPinnedIcon : Building2Icon}
                        size={13}
                        color={isActive ? '#fff' : DESIGN_TOKENS.colors.textSoft}
                      />
                      <Text
                        style={{
                          fontSize: 13,
                          fontWeight: '700',
                          color: isActive ? '#fff' : DESIGN_TOKENS.colors.textSoft,
                        }}>
                        {mode === 'events' ? 'Events' : 'Venues'}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>

              {/* Pin count badge */}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 5,
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor: DESIGN_TOKENS.colors.border,
                  backgroundColor: DESIGN_TOKENS.colors.surfaceRaised,
                  paddingHorizontal: 11,
                  paddingVertical: 6,
                }}>
                <Icon as={MapPinnedIcon} size={12} color={DESIGN_TOKENS.colors.textSoft} />
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: '700',
                    color: DESIGN_TOKENS.colors.textSoft,
                  }}>
                  {discoveryMode === 'events' ? filteredEvents.length : venues.length} pins
                </Text>
              </View>
            </View>

            {/* Divider */}
            <View
              style={{
                height: 1,
                backgroundColor: DESIGN_TOKENS.colors.border,
                marginHorizontal: 10,
              }}
            />

            {/* Events filters */}
            {discoveryMode === 'events' ? (
              <View
                style={{
                  position: 'relative',
                  paddingHorizontal: 10,
                  paddingVertical: 10,
                  gap: 8,
                }}>
                {/* Live / All today toggle */}
                <View
                  style={{
                    flexDirection: 'row',
                    borderRadius: 999,
                    backgroundColor: DESIGN_TOKENS.colors.surface,
                    borderWidth: 1,
                    borderColor: DESIGN_TOKENS.colors.border,
                    padding: 3,
                    gap: 2,
                  }}>
                  {(['live', 'today'] as const).map((mode) => {
                    const isActive = eventMapMode === mode;
                    return (
                      <Pressable
                        key={mode}
                        onPress={() => {
                          setEventMapMode(mode);
                          trackMapFilterApplied('event_map_mode', mode, 'web');
                        }}
                        style={{
                          flex: 1,
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 5,
                          paddingVertical: 6,
                          borderRadius: 999,
                          backgroundColor: isActive
                            ? DESIGN_TOKENS.colors.accentSoft
                            : 'transparent',
                          borderWidth: isActive ? 1 : 0,
                          borderColor: isActive ? DESIGN_TOKENS.colors.accentEdge : 'transparent',
                        }}>
                        <Icon
                          as={mode === 'live' ? FlameIcon : Clock3Icon}
                          size={12}
                          color={
                            isActive ? DESIGN_TOKENS.colors.accent : DESIGN_TOKENS.colors.textSoft
                          }
                        />
                        <Text
                          style={{
                            fontSize: 12,
                            fontWeight: '700',
                            color: isActive
                              ? DESIGN_TOKENS.colors.accent
                              : DESIGN_TOKENS.colors.textSoft,
                          }}>
                          {mode === 'live' ? 'Live now' : 'All today'}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>

                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <MapFilterButton
                    label="Entry"
                    value={eventEntryFilterSummary}
                    active={eventEntryFilter !== 'all'}
                    icon={TicketPercentIcon}
                    onPress={() =>
                      setMapFilterMenu((current) =>
                        current === 'eventEntry' ? null : 'eventEntry'
                      )
                    }
                  />
                  <MapFilterButton
                    label="Scene"
                    value={eventSceneFilterSummary}
                    active={Boolean(selectedEventTag)}
                    icon={SlidersHorizontalIcon}
                    onPress={() =>
                      setMapFilterMenu((current) =>
                        current === 'eventScene' ? null : 'eventScene'
                      )
                    }
                  />
                  {eventEntryFilter !== 'all' || selectedEventTag ? (
                    <Pressable
                      onPress={() => {
                        setSelectedId(null);
                        setEventEntryFilter('all');
                        setSelectedEventTag('');
                        setMapFilterMenu(null);
                        trackMapFilterApplied('entry_type', 'all', 'web');
                        trackMapFilterApplied('scene', 'all', 'web');
                      }}
                      style={{
                        width: 36,
                        height: 36,
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 999,
                        borderWidth: 1,
                        borderColor: DESIGN_TOKENS.colors.border,
                        backgroundColor: DESIGN_TOKENS.colors.surfaceRaised,
                      }}>
                      <Icon as={XIcon} size={14} color={DESIGN_TOKENS.colors.textSoft} />
                    </Pressable>
                  ) : null}
                </View>

                {mapFilterMenu === 'eventEntry' ? (
                  <MapFilterPopover left={0} top={92} title="Entry type">
                    {ENTRY_TYPE_FILTERS.map((filter) => (
                      <MapFilterOption
                        key={filter.value}
                        label={filter.value === 'all' ? 'Any entry' : filter.label}
                        active={eventEntryFilter === filter.value}
                        onPress={() => {
                          setSelectedId(null);
                          setEventEntryFilter(filter.value);
                          setMapFilterMenu(null);
                          trackMapFilterApplied('entry_type', filter.value, 'web');
                        }}
                      />
                    ))}
                  </MapFilterPopover>
                ) : null}

                {mapFilterMenu === 'eventScene' ? (
                  <MapFilterPopover left={0} top={92} title="Scene">
                    <MapFilterOption
                      label="All scenes"
                      active={!selectedEventTag}
                      onPress={() => {
                        setSelectedId(null);
                        setSelectedEventTag('');
                        setMapFilterMenu(null);
                        trackMapFilterApplied('scene', 'all', 'web');
                      }}
                    />
                    {eventSceneFilterGroups.map((group) => (
                      <View key={group.key} style={{ gap: 6, paddingTop: 6 }}>
                        <Text variant="small" className="px-1 font-semibold text-muted-foreground">
                          {group.label}
                        </Text>
                        {group.tags.map((tag) => (
                          <MapFilterOption
                            key={tag.value}
                            label={tag.label}
                            active={selectedEventTag === tag.value}
                            onPress={() => {
                              setSelectedId(null);
                              setSelectedEventTag((current) =>
                                current === tag.value ? '' : tag.value
                              );
                              setMapFilterMenu(null);
                              trackMapFilterApplied(
                                'scene',
                                selectedEventTag === tag.value ? 'all' : tag.value,
                                'web'
                              );
                            }}
                          />
                        ))}
                      </View>
                    ))}
                  </MapFilterPopover>
                ) : null}
              </View>
            ) : null}

            {/* Venue filters */}
            {discoveryMode === 'venues' ? (
              <View style={{ position: 'relative', paddingHorizontal: 10, paddingVertical: 10 }}>
                <View
                  style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                  <MapFilterButton
                    label="Category"
                    value={venueFilterSummary}
                    active={selectedVenueCategories.length > 0}
                    icon={SlidersHorizontalIcon}
                    onPress={() =>
                      setMapFilterMenu((current) =>
                        current === 'venueCategory' ? null : 'venueCategory'
                      )
                    }
                  />
                  <MapFilterButton
                    label="Deals"
                    value={showDealVenuesOnly ? dealFilterSummary : 'Any venue'}
                    icon={TicketPercentIcon}
                    active={showDealVenuesOnly}
                    onPress={() =>
                      setMapFilterMenu((current) =>
                        current === 'venueDeals' ? null : 'venueDeals'
                      )
                    }
                  />
                  <MapFilterButton
                    label="Timing"
                    value={venueActivityFilterSummary}
                    icon={Clock3Icon}
                    active={venueActivityFilter !== 'any'}
                    onPress={() =>
                      setMapFilterMenu((current) =>
                        current === 'venueActivity' ? null : 'venueActivity'
                      )
                    }
                  />
                  {selectedVenueCategories.length > 0 ||
                  showDealVenuesOnly ||
                  venueActivityFilter !== 'any' ? (
                    <Pressable
                      onPress={() => {
                        setSelectedVenueId(null);
                        setSelectedVenueCategories([]);
                        setShowDealVenuesOnly(false);
                        setSelectedDealCategories(MAP_DEAL_FILTER_CATEGORIES);
                        setVenueActivityFilter('any');
                        setMapFilterMenu(null);
                        trackMapFilterApplied('category', 'all', 'web');
                        trackMapFilterApplied('venue_deals', 'all', 'web');
                        trackMapFilterApplied('venue_deal_categories', 'all', 'web');
                        trackMapFilterApplied('venue_activity', 'any', 'web');
                      }}
                      style={{
                        width: 36,
                        height: 36,
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 999,
                        borderWidth: 1,
                        borderColor: DESIGN_TOKENS.colors.border,
                        backgroundColor: DESIGN_TOKENS.colors.surfaceRaised,
                      }}>
                      <Icon as={XIcon} size={14} color={DESIGN_TOKENS.colors.textSoft} />
                    </Pressable>
                  ) : null}
                </View>

                {mapFilterMenu === 'venueCategory' ? (
                  <MapFilterPopover left={0} top={44} title="Venue type">
                    <MapFilterOption
                      label="All venues"
                      active={selectedVenueCategories.length === 0}
                      onPress={() => {
                        setSelectedVenueId(null);
                        setSelectedVenueCategories([]);
                        setMapFilterMenu(null);
                        trackMapFilterApplied('category', 'all', 'web');
                      }}
                    />
                    {MAP_VENUE_CATEGORY_GROUPS.map((group) => (
                      <MapFilterOption
                        key={group.label}
                        label={group.label}
                        active={areSameVenueCategorySelection(
                          selectedVenueCategories,
                          group.values
                        )}
                        onPress={() => applyVenueCategoryGroup(group.values)}
                      />
                    ))}
                  </MapFilterPopover>
                ) : null}

                {mapFilterMenu === 'venueDeals' ? (
                  <MapFilterPopover left={158} top={44} title="Deal filters">
                    <MapFilterOption
                      label="Any venue"
                      active={!showDealVenuesOnly}
                      onPress={() => {
                        setSelectedVenueId(null);
                        setShowDealVenuesOnly(false);
                        setSelectedDealCategories(MAP_DEAL_FILTER_CATEGORIES);
                        setMapFilterMenu(null);
                        trackMapFilterApplied('venue_deals', 'all', 'web');
                        trackMapFilterApplied('venue_deal_categories', 'all', 'web');
                      }}
                    />
                    <MapFilterOption
                      label="Venues with deals"
                      active={showDealVenuesOnly}
                      onPress={() => {
                        setSelectedVenueId(null);
                        setShowDealVenuesOnly(true);
                        trackMapFilterApplied('venue_deals', 'deals_only', 'web');
                      }}
                    />
                    {showDealVenuesOnly ? (
                      <View style={{ gap: 6, paddingTop: 6 }}>
                        <Text variant="small" className="px-1 font-semibold text-muted-foreground">
                          Deal types
                        </Text>
                        {MAP_DEAL_FILTER_CATEGORIES.map((category) => (
                          <MapFilterOption
                            key={category}
                            label={getMapDealCategoryLabel(category)}
                            active={selectedDealCategories.includes(category)}
                            onPress={() => toggleDealCategory(category)}
                          />
                        ))}
                      </View>
                    ) : null}
                  </MapFilterPopover>
                ) : null}

                {mapFilterMenu === 'venueActivity' ? (
                  <MapFilterPopover left={100} top={44} title="Venue timing">
                    {(['any', 'live', 'upcoming'] as const).map((filter) => (
                      <MapFilterOption
                        key={filter}
                        label={getVenueActivityFilterLabel(filter)}
                        active={venueActivityFilter === filter}
                        onPress={() => {
                          setSelectedVenueId(null);
                          setVenueActivityFilter(filter);
                          setMapFilterMenu(null);
                          trackMapFilterApplied('venue_activity', filter, 'web');
                        }}
                      />
                    ))}
                  </MapFilterPopover>
                ) : null}
              </View>
            ) : null}
          </View>
        </View>

        {/* Floating recenter button */}
        <Pressable
          onPress={() => recenterRef.current?.()}
          style={{
            position: 'absolute',
            bottom: selectedEvent || selectedVenue ? undefined : isCompactWeb ? 82 : 24,
            top: selectedEvent || selectedVenue ? 80 : undefined,
            right: isCompactWeb ? 12 : 16,
            zIndex: 20,
            width: 44,
            height: 44,
            borderRadius: 22,
            backgroundColor: DESIGN_TOKENS.colors.surface,
            borderWidth: 1,
            borderColor: DESIGN_TOKENS.colors.borderStrong,
            alignItems: 'center',
            justifyContent: 'center',
            shadowColor: '#000',
            shadowOpacity: 0.3,
            shadowRadius: 8,
            shadowOffset: { width: 0, height: 4 },
          }}>
          <Icon as={LocateFixedIcon} size={18} color={DESIGN_TOKENS.colors.text} />
        </Pressable>

        {/* Event detail panel — floats over map, anchored bottom-right */}
        {selectedEvent ? (
          <View
            style={{
              position: 'absolute',
              left: isCompactWeb ? 12 : undefined,
              right: isCompactWeb ? 12 : 16,
              bottom: isCompactWeb ? 76 : 16,
              width: isCompactWeb ? undefined : 360,
              maxHeight: (isCompactWeb ? '56%' : 'calc(100% - 96px)') as any,
              backgroundColor: DESIGN_TOKENS.colors.surfaceOverlay,
              borderRadius: 24,
              borderWidth: 1,
              borderColor: DESIGN_TOKENS.colors.borderStrong,
              shadowColor: '#000',
              shadowOpacity: 0.4,
              shadowRadius: 24,
              shadowOffset: { width: 0, height: 8 },
              overflow: 'hidden',
              zIndex: 20,
            }}>
            <ScrollView
              contentContainerStyle={{ padding: 18 }}
              showsVerticalScrollIndicator={false}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: 14,
                }}>
                <EventPoster
                  event={selectedEvent}
                  className="h-[124px] w-[88px] rounded-[18px]"
                  showMeta={false}
                />
                <View style={{ flex: 1, gap: 8, paddingRight: 38 }}>
                  <Text
                    style={{
                      color: DESIGN_TOKENS.colors.textMuted,
                      fontSize: 12,
                      fontWeight: '700',
                    }}>
                    {selectedRelativeTime?.label ?? formatEventDayLabel(selectedEvent.starts_at)}
                  </Text>
                  <Text variant="large" className="font-semibold" numberOfLines={3}>
                    {selectedEvent.title}
                  </Text>
                  <Text variant="muted" numberOfLines={2}>
                    At {selectedEvent.venue_name}
                  </Text>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 }}>
                    <MetaBadge label={formatEventDayLabel(selectedEvent.starts_at)} />
                    {selectedEntryLabel ? (
                      <MetaBadge label={selectedEntryLabel} tone="warm" />
                    ) : null}
                    {selectedDistanceLabel ? (
                      <MetaBadge label={selectedDistanceLabel} tone="warm" />
                    ) : null}
                  </View>
                </View>
                <Pressable
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    width: 36,
                    height: 36,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 18,
                    borderWidth: 1,
                    borderColor: DESIGN_TOKENS.colors.border,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                  }}
                  onPress={closePanel}>
                  <Icon as={XIcon} size={14} className="text-muted-foreground" />
                </Pressable>
              </View>

              <View style={{ gap: 10, marginTop: 16 }}>
                <PreviewInfoRow
                  icon={Clock3Icon}
                  label={formatEventTimeRange(selectedEvent.starts_at, selectedEvent.ends_at)}
                  muted={selectedRelativeTime?.label}
                />
                <PreviewInfoRow
                  icon={MapPinIcon}
                  label={selectedEvent.venue_name}
                  muted={selectedEvent.address || undefined}
                />
              </View>

              <View
                style={{
                  marginTop: 14,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}>
                <View style={{ flex: 1 }}>
                  <Text variant="muted">Hosted by</Text>
                  <Text variant="small" className="mt-1 font-semibold" numberOfLines={1}>
                    {selectedEvent.host.display_name}
                  </Text>
                </View>
                <Text
                  style={{
                    color: DESIGN_TOKENS.colors.accent,
                    fontSize: 13,
                    fontWeight: '800',
                  }}>
                  {selectedEvent.rsvp_count} going
                </Text>
              </View>

              {selectedMetadata.length ? (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                  {selectedMetadata.map((label) => (
                    <MetaBadge key={label} label={label} />
                  ))}
                </View>
              ) : null}

              <Text variant="muted" numberOfLines={4} style={{ marginTop: 12 }}>
                {selectedEvent.summary}
              </Text>

              {/* Actions */}
              <View style={{ flexDirection: 'row', gap: 10, marginTop: 16 }}>
                <Pressable
                  style={{
                    height: 44,
                    flex: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 14,
                    backgroundColor: DESIGN_TOKENS.colors.accent,
                  }}
                  onPress={() =>
                    router.navigate(getEventHref(selectedEvent.id, selectedEvent.title) as never)
                  }>
                  <Text className="font-semibold text-accent-foreground">Open event</Text>
                </Pressable>
              </View>

              <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                <Pressable
                  style={{
                    height: 40,
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 7,
                    borderRadius: 14,
                    borderWidth: 1,
                    borderColor: DESIGN_TOKENS.colors.border,
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    paddingHorizontal: 14,
                  }}
                  onPress={handleDirections}>
                  <Icon as={NavigationIcon} size={13} color={DESIGN_TOKENS.colors.textSoft} />
                  <Text className="font-semibold">Directions</Text>
                </Pressable>
                <Pressable
                  style={{
                    height: 40,
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 7,
                    borderRadius: 14,
                    borderWidth: 1,
                    borderColor: DESIGN_TOKENS.colors.border,
                    backgroundColor: 'rgba(255,255,255,0.03)',
                  }}
                  onPress={handleEventShare}>
                  <Icon as={Share2Icon} size={13} className="text-muted-foreground" />
                  <Text className="font-semibold">Share</Text>
                </Pressable>
                <Pressable
                  style={{
                    height: 40,
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 7,
                    borderRadius: 14,
                    borderWidth: 1,
                    borderColor: DESIGN_TOKENS.colors.border,
                    backgroundColor: 'rgba(255,255,255,0.03)',
                  }}
                  onPress={() =>
                    router.push(
                      getPublicIdentityHref({
                        username: selectedEvent.host.username,
                        is_venue: selectedEvent.host.is_venue,
                        venue_id: selectedEvent.venue_id,
                      })
                    )
                  }>
                  <Icon as={SparklesIcon} size={13} className="text-muted-foreground" />
                  <Text className="font-semibold">Host</Text>
                </Pressable>
              </View>
            </ScrollView>
          </View>
        ) : null}

        {selectedVenue ? (
          <View
            style={{
              position: 'absolute',
              left: isCompactWeb ? 12 : undefined,
              right: isCompactWeb ? 12 : 16,
              bottom: isCompactWeb ? 76 : 16,
              width: isCompactWeb ? undefined : 360,
              maxHeight: (isCompactWeb ? '56%' : 'calc(100% - 96px)') as any,
              backgroundColor: DESIGN_TOKENS.colors.surfaceOverlay,
              borderRadius: 24,
              borderWidth: 1,
              borderColor: DESIGN_TOKENS.colors.borderStrong,
              shadowColor: '#000',
              shadowOpacity: 0.4,
              shadowRadius: 24,
              shadowOffset: { width: 0, height: 8 },
              overflow: 'hidden',
              zIndex: 20,
            }}>
            <ScrollView
              contentContainerStyle={{ padding: 20 }}
              showsVerticalScrollIndicator={false}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 12,
                }}>
                <View style={{ flex: 1, gap: 4 }}>
                  <Text variant="large" className="font-semibold" numberOfLines={2}>
                    {selectedVenue.name}
                  </Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <View
                      style={{
                        width: 26,
                        height: 26,
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 13,
                        backgroundColor: `#${selectedVenueCategoryConfig.bgColor}`,
                        borderWidth: 1,
                        borderColor: `#${selectedVenueCategoryConfig.color}`,
                      }}>
                      <Icon
                        as={selectedVenueCategoryConfig.icon}
                        size={13}
                        color={`#${selectedVenueCategoryConfig.color}`}
                      />
                    </View>
                    <Text variant="muted">{selectedVenueCategoryConfig.label}</Text>
                  </View>
                </View>
                <Pressable
                  style={{
                    width: 36,
                    height: 36,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 18,
                    borderWidth: 1,
                    borderColor: DESIGN_TOKENS.colors.border,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                  }}
                  onPress={closePanel}>
                  <Icon as={XIcon} size={14} className="text-muted-foreground" />
                </Pressable>
              </View>

              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
                <MetaBadge
                  label={formatApproximateDistanceLabel(selectedVenue.distance_miles, userOrigin)}
                  tone="warm"
                />
                {selectedVenue.has_live_event ? <MetaBadge label="Live event" tone="warm" /> : null}
                {selectedVenue.deals.length ? (
                  <MetaBadge
                    label={`${selectedVenue.deals.length} ${
                      selectedVenue.deals.length === 1 ? 'deal' : 'deals'
                    }`}
                    tone="warm"
                  />
                ) : null}
                {selectedVenue.upcoming_event_count ? (
                  <MetaBadge label={`${selectedVenue.upcoming_event_count} upcoming`} />
                ) : null}
              </View>

              <View style={{ gap: 10, marginTop: 16 }}>
                <PreviewInfoRow
                  icon={MapPinIcon}
                  label={selectedVenue.address ?? 'Address unavailable'}
                />
              </View>

              {selectedVenueDeals.length ? (
                <View style={{ gap: 10, marginTop: 18 }}>
                  <MapPreviewSectionHeader label="Deals" detail="Live or next up" />
                  {selectedVenueDeals.map((deal) => {
                    const timing = getDealTiming(deal);
                    return (
                      <MapVenueDealPreviewCard
                        key={deal.id}
                        deal={deal}
                        isLive={timing.state === 'live'}
                        timingLabel={timing.label}
                      />
                    );
                  })}
                </View>
              ) : null}

              <View style={{ gap: 10, marginTop: 18 }}>
                <MapPreviewSectionHeader
                  label="Upcoming"
                  detail={
                    selectedVenue.upcoming_event_count
                      ? `${selectedVenue.upcoming_event_count} this week`
                      : undefined
                  }
                />
                {selectedVenueEventsQuery.isPending ? (
                  <MapVenuePreviewEmptyState title="Loading lineup" compact />
                ) : selectedVenueEventsQuery.isError ? (
                  <MapVenuePreviewEmptyState
                    title="Couldn’t load the lineup"
                    body="The map still has the venue. Try refreshing the lineup for the latest events."
                    actionLabel="Retry"
                    onAction={() => {
                      selectedVenueEventsQuery.refetch().catch(() => undefined);
                    }}
                  />
                ) : selectedVenueEvents.length ? (
                  selectedVenueEvents.map((event) => (
                    <MapVenueEventPreviewRow
                      key={event.id}
                      event={event}
                      onPress={() => router.navigate(getEventHref(event.id, event.title) as never)}
                    />
                  ))
                ) : selectedVenueDeals.length ? (
                  <MapVenuePreviewEmptyState
                    title="No upcoming events listed"
                    body="Deals above may still be active."
                    compact
                  />
                ) : (
                  <MapVenuePreviewEmptyState
                    title="Quiet on Radious right now"
                    body="No events or deals are listed for this venue yet."
                  />
                )}
              </View>

              <View style={{ flexDirection: 'row', gap: 10, marginTop: 16 }}>
                <Pressable
                  style={{
                    height: 44,
                    flex: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 14,
                    backgroundColor: DESIGN_TOKENS.colors.accent,
                  }}
                  onPress={() =>
                    router.navigate(
                      getVenueHref(selectedVenue.id, {}, { name: selectedVenue.name }) as never
                    )
                  }>
                  <Text className="font-semibold text-accent-foreground">Open venue</Text>
                </Pressable>
                <Pressable
                  disabled={!selectedVenue.address}
                  style={{
                    height: 44,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 7,
                    borderRadius: 14,
                    borderWidth: 1,
                    borderColor: DESIGN_TOKENS.colors.border,
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    opacity: selectedVenue.address ? 1 : 0.45,
                    paddingHorizontal: 14,
                  }}
                  onPress={handleVenueDirections}>
                  <Icon as={NavigationIcon} size={13} color={DESIGN_TOKENS.colors.textSoft} />
                  <Text className="font-semibold">Directions</Text>
                </Pressable>
              </View>
            </ScrollView>
          </View>
        ) : null}
      </View>
    </AppScreen>
  );
}

function PreviewInfoRow({
  icon,
  label,
  muted,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
  muted?: string;
}) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
      <View
        style={{
          marginTop: 2,
          width: 26,
          height: 26,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 13,
          backgroundColor: 'rgba(255,255,255,0.04)',
        }}>
        <Icon as={icon} size={13} className="text-muted-foreground" />
      </View>
      <View style={{ flex: 1, gap: 2 }}>
        <Text variant="small">{label}</Text>
        {muted ? (
          <Text variant="muted" numberOfLines={1}>
            {muted}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

function getAbsoluteHref(href: any) {
  const path = hrefToPath(href);
  const origin =
    typeof globalThis.location?.origin === 'string'
      ? globalThis.location.origin
      : APP_CONFIG.webBaseURL;
  return `${origin}${path}`;
}

function hrefToPath(href: any) {
  const pathname = String(href?.pathname ?? '/events');
  const params = href?.params && typeof href.params === 'object' ? href.params : {};
  const id = typeof params.id === 'string' ? params.id : '';
  const username = typeof params.username === 'string' ? params.username : '';
  const concretePath = pathname.replace('[id]', id).replace('[username]', username);
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (key === 'id' || key === 'username' || value == null) return;
    query.set(key, String(value));
  });

  const suffix = query.toString();
  return suffix ? `${concretePath}?${suffix}` : concretePath;
}

function MapFilterButton({
  active,
  icon,
  label: _label,
  onPress,
  value,
}: {
  active: boolean;
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
  onPress: () => void;
  value: string;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={{
        flexBasis: 0,
        flexGrow: 1,
        minWidth: 88,
        height: 38,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 5,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: active ? DESIGN_TOKENS.colors.accentEdge : DESIGN_TOKENS.colors.border,
        backgroundColor: active
          ? DESIGN_TOKENS.colors.accentSoft
          : DESIGN_TOKENS.colors.surfaceRaised,
        paddingHorizontal: 10,
      }}>
      <Icon
        as={icon}
        size={12}
        color={active ? DESIGN_TOKENS.colors.accent : DESIGN_TOKENS.colors.textSoft}
      />
      <Text
        style={{
          flexShrink: 1,
          fontSize: 12,
          fontWeight: '700',
          color: active ? DESIGN_TOKENS.colors.accent : DESIGN_TOKENS.colors.text,
        }}
        numberOfLines={1}>
        {value}
      </Text>
      <Icon
        as={ChevronDownIcon}
        size={10}
        color={active ? DESIGN_TOKENS.colors.accent : DESIGN_TOKENS.colors.textSoft}
      />
    </Pressable>
  );
}

function MapFilterPopover({
  children,
  left,
  title,
  top,
}: {
  children: React.ReactNode;
  left: number;
  title: string;
  top: number;
}) {
  return (
    <View
      nativeID={MAP_FILTER_POPOVER_ID}
      style={{
        position: 'absolute',
        left,
        top,
        width: 260,
        maxHeight: 380,
        overflow: 'hidden',
        borderRadius: 18,
        borderWidth: 1,
        borderColor: DESIGN_TOKENS.colors.borderStrong,
        backgroundColor: DESIGN_TOKENS.colors.surfaceOverlay,
        padding: 10,
        zIndex: 80,
        elevation: 80,
        shadowColor: '#000',
        shadowOpacity: 0.34,
        shadowRadius: 22,
        shadowOffset: { width: 0, height: 10 },
      }}>
      <Text variant="small" className="px-2 pb-2 font-semibold text-muted-foreground">
        {title}
      </Text>
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ gap: 6 }}>
        {children}
      </ScrollView>
    </View>
  );
}

function MapFilterOption({
  active,
  compact = false,
  label,
  onPress,
}: {
  active: boolean;
  compact?: boolean;
  label: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={{
        minHeight: compact ? 34 : 38,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        borderRadius: compact ? 999 : 12,
        borderWidth: compact ? 1 : 0,
        borderColor: active ? DESIGN_TOKENS.colors.accentEdge : DESIGN_TOKENS.colors.border,
        backgroundColor: active ? DESIGN_TOKENS.colors.accentSoft : 'transparent',
        paddingHorizontal: compact ? 13 : 10,
      }}>
      <Text className="font-semibold" numberOfLines={1}>
        {label}
      </Text>
      {active && !compact ? (
        <Icon as={CheckIcon} size={14} color={DESIGN_TOKENS.colors.accent} />
      ) : null}
    </Pressable>
  );
}

function MapPreviewSectionHeader({ detail, label }: { detail?: string; label: string }) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}>
      <Text
        style={{
          color: DESIGN_TOKENS.colors.textMuted,
          fontSize: 11,
          fontWeight: '800',
          letterSpacing: 0.9,
          textTransform: 'uppercase',
        }}>
        {label}
      </Text>
      {detail ? (
        <Text
          style={{ color: DESIGN_TOKENS.colors.textSoft, fontSize: 11, fontWeight: '700' }}
          numberOfLines={1}>
          {detail}
        </Text>
      ) : null}
    </View>
  );
}

function MapVenueDealPreviewCard({
  deal,
  isLive,
  timingLabel,
}: {
  deal: VenuePublicDeal;
  isLive: boolean;
  timingLabel: string;
}) {
  const accentColor = getMapDealCategoryAccent(deal.category);
  const categoryLabel = getMapDealCategoryLabel(deal.category);

  return (
    <View
      style={{
        minHeight: 82,
        overflow: 'hidden',
        borderRadius: 20,
        borderWidth: 1,
        borderColor: withOpacity(accentColor, 0.28),
        backgroundColor: DESIGN_TOKENS.colors.surface,
        paddingHorizontal: 14,
        paddingVertical: 12,
        gap: 8,
      }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: withOpacity(accentColor, 0.34),
            backgroundColor: withOpacity(accentColor, 0.14),
            paddingHorizontal: 9,
            paddingVertical: 4,
          }}>
          <Icon as={TicketPercentIcon} size={11} color={accentColor} />
          <Text style={{ color: accentColor, fontSize: 10, fontWeight: '900' }} numberOfLines={1}>
            {categoryLabel}
          </Text>
        </View>
        {isLive ? (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 5,
              borderRadius: 999,
              backgroundColor: 'rgba(239,78,46,0.18)',
              paddingHorizontal: 8,
              paddingVertical: 4,
            }}>
            <Icon as={FlameIcon} size={10} color="#ff7a55" />
            <Text style={{ color: '#ff7a55', fontSize: 10, fontWeight: '900' }}>LIVE</Text>
          </View>
        ) : null}
      </View>
      <View style={{ gap: 3 }}>
        <Text className="font-bold" numberOfLines={1}>
          {deal.title || 'Venue deal'}
        </Text>
        <Text variant="muted" numberOfLines={1}>
          {timingLabel} · {formatDealTimeRange(deal)}
        </Text>
      </View>
    </View>
  );
}

function MapVenueEventPreviewRow({ event, onPress }: { event: RadiousEvent; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={{
        minHeight: 74,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: DESIGN_TOKENS.colors.border,
        backgroundColor: DESIGN_TOKENS.colors.surface,
        padding: 10,
      }}>
      <EventPoster event={event} className="h-[54px] w-[42px] rounded-[12px]" showMeta={false} />
      <View style={{ flex: 1, gap: 4 }}>
        <Text className="font-semibold" numberOfLines={1}>
          {event.title}
        </Text>
        <Text variant="muted" numberOfLines={1}>
          {formatEventDayLabel(event.starts_at)} ·{' '}
          {formatEventTimeRange(event.starts_at, event.ends_at)}
        </Text>
      </View>
    </Pressable>
  );
}

function MapVenuePreviewEmptyState({
  actionLabel,
  body,
  compact = false,
  onAction,
  title,
}: {
  actionLabel?: string;
  body?: string;
  compact?: boolean;
  onAction?: () => void;
  title: string;
}) {
  return (
    <View
      style={{
        borderRadius: 18,
        borderWidth: 1,
        borderColor: DESIGN_TOKENS.colors.border,
        backgroundColor: DESIGN_TOKENS.colors.surface,
        padding: compact ? 12 : 14,
        gap: 4,
      }}>
      <Text className="font-semibold" numberOfLines={1}>
        {title}
      </Text>
      {body ? <Text variant="muted">{body}</Text> : null}
      {actionLabel && onAction ? (
        <Pressable
          onPress={onAction}
          style={{
            alignSelf: 'flex-start',
            minHeight: 44,
            marginTop: 6,
            borderRadius: 999,
            backgroundColor: DESIGN_TOKENS.colors.accent,
            paddingHorizontal: 14,
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Text className="font-semibold text-accent-foreground">{actionLabel}</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

function MapStatusOverlay({
  isError,
  isLoading,
  isEmpty,
  hasFilter,
  mode,
  subject,
  onRetry,
  onClearFilter,
  onShowToday,
}: {
  isError: boolean;
  isLoading: boolean;
  isEmpty: boolean;
  hasFilter: boolean;
  mode: EventMapMode;
  subject: WebDiscoveryMode;
  onRetry: () => void;
  onClearFilter: () => void;
  onShowToday: () => void;
}) {
  if (isLoading) {
    return (
      <FloatingMapMessage
        title={subject === 'events' ? 'Loading map events' : 'Loading nearby venues'}
        body="Finding what is happening nearby."
      />
    );
  }

  if (isError) {
    return (
      <FloatingMapMessage
        title={subject === 'events' ? 'Couldn’t load map events' : 'Couldn’t load venues'}
        body="The map is still available. Try refreshing the pins."
        actionLabel="Retry"
        onAction={onRetry}
      />
    );
  }

  if (isEmpty) {
    const actionLabel = hasFilter
      ? 'Clear filter'
      : subject === 'events' && mode === 'live'
        ? 'Show today'
        : undefined;
    const action = hasFilter
      ? onClearFilter
      : subject === 'events' && mode === 'live'
        ? onShowToday
        : undefined;
    return (
      <FloatingMapMessage
        title={subject === 'events' ? 'No events in this map view' : 'No venues in this map view'}
        body="Move the map or loosen the filters to discover more."
        actionLabel={actionLabel}
        onAction={action}
      />
    );
  }

  return null;
}

function FloatingMapMessage({
  title,
  body,
  actionLabel,
  compact = false,
  onAction,
}: {
  title: string;
  body?: string;
  actionLabel?: string;
  compact?: boolean;
  onAction?: () => void;
}) {
  return (
    <View
      pointerEvents="box-none"
      style={{
        position: 'absolute',
        left: 16,
        right: 16,
        top: compact ? 150 : '45%',
        zIndex: 15,
        alignItems: 'center',
      }}>
      <View
        style={{
          maxWidth: compact ? 220 : 360,
          borderRadius: 18,
          borderWidth: 1,
          borderColor: DESIGN_TOKENS.colors.borderStrong,
          backgroundColor: DESIGN_TOKENS.colors.surfaceOverlay,
          paddingHorizontal: compact ? 14 : 18,
          paddingVertical: compact ? 10 : 16,
          shadowColor: '#000',
          shadowOpacity: 0.28,
          shadowRadius: 18,
          shadowOffset: { width: 0, height: 8 },
        }}>
        <Text className="text-center font-semibold">{title}</Text>
        {body ? (
          <Text variant="muted" className="mt-1 text-center">
            {body}
          </Text>
        ) : null}
        {actionLabel && onAction ? (
          <Pressable
            onPress={onAction}
            style={{
              alignSelf: 'center',
              marginTop: 12,
              borderRadius: 999,
              backgroundColor: DESIGN_TOKENS.colors.accent,
              paddingHorizontal: 16,
              paddingVertical: 8,
            }}>
            <Text className="font-semibold text-accent-foreground">{actionLabel}</Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

function webRegionChangedMeaningfully(prev: WebMapRegion, next: WebMapRegion) {
  const centerMoved =
    Math.abs(prev.latitude - next.latitude) > prev.latitudeDelta * 0.15 ||
    Math.abs(prev.longitude - next.longitude) > prev.longitudeDelta * 0.15;
  const zoomChanged =
    Math.abs(prev.latitudeDelta - next.latitudeDelta) > prev.latitudeDelta * 0.2 ||
    Math.abs(prev.longitudeDelta - next.longitudeDelta) > prev.longitudeDelta * 0.2;
  return centerMoved || zoomChanged;
}

function getSafeVenueDeals(deals: VenuePublicDeal[] | null | undefined) {
  return Array.isArray(deals) ? deals : [];
}

function isVenueMapDeal(deal: VenuePublicDeal) {
  const state = getDealTiming(deal).state;
  return state === 'live' || state === 'scheduled';
}

function hasVenueMapDealByCategories(
  deals: VenuePublicDeal[] | null | undefined,
  categories: Set<VenueDealCategory>
) {
  return getSafeVenueDeals(deals).some(
    (deal) => isVenueMapDeal(deal) && categories.has(deal.category)
  );
}

function eventMatchesEntryFilter(event: RadiousEvent, filter: EntryTypeFilter) {
  switch (filter) {
    case 'all':
      return true;
    case EntryTypeFree:
      return event.entry_type === EntryTypeFree || event.entry_type === EntryTypeFreeRSVP;
    case EntryTypeRSVP:
      return event.entry_type === EntryTypeRSVP || event.entry_type === EntryTypeFreeRSVP;
    case EntryTypeCover:
      return event.entry_type === EntryTypeCover;
    case EntryTypeTicketed:
      return event.entry_type === EntryTypeTicketed;
    default:
      return true;
  }
}

function getMapEntryFilterLabel(filter: EntryTypeFilter) {
  if (filter === 'all') {
    return 'Any entry';
  }
  return ENTRY_TYPE_FILTERS.find((entryFilter) => entryFilter.value === filter)?.label ?? 'Entry';
}

function buildMapSceneFilterGroups(
  events: RadiousEvent[],
  catalogGroups?: {
    category_group: string;
    label: string;
    tags: { label: string; slug: string }[];
  }[]
): EventTagGroup[] {
  const activeTags = new Set(
    events.flatMap((event) =>
      event.tags.flatMap((tag) => {
        const trimmed = tag.trim();
        return trimmed ? [trimmed] : [];
      })
    )
  );

  if (catalogGroups?.length) {
    return catalogGroups.flatMap((group) => {
      const tags = group.tags.flatMap((tag) =>
        activeTags.has(tag.slug)
          ? [
              {
                value: tag.slug,
                label: tag.label,
                colors: ['#ef4e2e'],
              },
            ]
          : []
      );

      return tags.length
        ? [
            {
              key: group.category_group,
              label: group.label,
              tags,
            },
          ]
        : [];
    });
  }

  const tags = Array.from(activeTags)
    .sort((left, right) => getEventTagLabel(left).localeCompare(getEventTagLabel(right)))
    .map((tag) => ({
      value: tag,
      label: getEventTagLabel(tag),
      colors: ['#ef4e2e'],
    }));

  return tags.length ? [{ key: 'active-map', label: 'In this map', tags }] : [];
}

function areSameVenueCategorySelection(current: string[], next: VenueCategory[]) {
  return current.length === next.length && next.every((category) => current.includes(category));
}

function getMapVenueCategorySummary(categories: string[]) {
  if (categories.length === 0) {
    return 'All types';
  }

  const group = MAP_VENUE_CATEGORY_GROUPS.find((categoryGroup) =>
    areSameVenueCategorySelection(categories, categoryGroup.values)
  );
  return group?.label ?? `${categories.length} types`;
}

function getVenueActivityFilterLabel(filter: VenueActivityFilter) {
  switch (filter) {
    case 'live':
      return 'Live now';
    case 'upcoming':
      return 'Upcoming';
    default:
      return 'Any time';
  }
}

function getMapDealCategoryLabel(category: VenueDealCategory) {
  return getVenueDealCategoryLabel(category, 'short');
}

function getMapDealCategoryAccent(category: VenueDealCategory) {
  return getVenueDealCategoryColor(category);
}

function withOpacity(color: string, opacity: number) {
  if (color.startsWith('rgba(')) {
    return color.replace(/rgba\(([^,]+),([^,]+),([^,]+),([^)]+)\)/, (_match, r, g, b) => {
      return `rgba(${r.trim()},${g.trim()},${b.trim()},${opacity})`;
    });
  }

  if (color.startsWith('#') && color.length === 7) {
    const r = Number.parseInt(color.slice(1, 3), 16);
    const g = Number.parseInt(color.slice(3, 5), 16);
    const b = Number.parseInt(color.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${opacity})`;
  }

  return color;
}
